import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, ShoppingCart, DollarSign, Clock, ArrowLeft, 
  Users, Tag, TrendingUp, AlertTriangle, BarChart3 
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, isAdmin, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    pending_orders: 0,
    completed_orders: 0,
    total_revenue: 0,
    total_customers: 0,
    active_coupons: 0,
    cod_revenue: 0,
    online_revenue: 0,
    recent_orders: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth loading to complete before checking token
    if (authLoading) return;
    
    if (!token) {
      navigate('/login?redirect=/admin');
      return;
    }
    fetchStats();
  }, [token, navigate, authLoading]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats.total_products,
      icon: <Package className="w-6 h-6" />,
      color: 'bg-blue-500',
      link: '/admin/products'
    },
    {
      title: 'Total Orders',
      value: stats.total_orders,
      icon: <ShoppingCart className="w-6 h-6" />,
      color: 'bg-green-500',
      link: '/admin/orders'
    },
    {
      title: 'Pending Orders',
      value: stats.pending_orders,
      icon: <Clock className="w-6 h-6" />,
      color: stats.pending_orders > 0 ? 'bg-orange-500' : 'bg-gray-400',
      link: '/admin/orders'
    },
    {
      title: 'Total Revenue',
      value: `₹${stats.total_revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      icon: <DollarSign className="w-6 h-6" />,
      color: 'bg-primary',
      link: '/admin/orders'
    },
    {
      title: 'Total Customers',
      value: stats.total_customers,
      icon: <Users className="w-6 h-6" />,
      color: 'bg-purple-500',
      link: '/admin/customers'
    },
    {
      title: 'Active Coupons',
      value: stats.active_coupons,
      icon: <Tag className="w-6 h-6" />,
      color: 'bg-pink-500',
      link: '/admin/coupons'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Products',
      description: 'Add, edit, or remove products from your catalog',
      icon: <Package className="w-10 h-10 text-primary" />,
      link: '/admin/products'
    },
    {
      title: 'Manage Orders',
      description: 'View and update order status, tracking info',
      icon: <ShoppingCart className="w-10 h-10 text-primary" />,
      link: '/admin/orders'
    },
    {
      title: 'Manage Coupons',
      description: 'Create and manage discount codes',
      icon: <Tag className="w-10 h-10 text-primary" />,
      link: '/admin/coupons'
    },
    {
      title: 'View Customers',
      description: 'Customer information and purchase history',
      icon: <Users className="w-10 h-10 text-primary" />,
      link: '/admin/customers'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container-custom py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="admin-heading">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-1">Welcome back, {user?.name || 'Admin'}</p>
            </div>
            <Link to="/">
              <button className="btn-outline flex items-center space-x-2" data-testid="back-to-site-button">
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Site</span>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <div 
                className={`${stat.color} text-white rounded-2xl p-5 shadow-card hover:shadow-hover transition-all hover:scale-105`} 
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    {stat.icon}
                  </div>
                </div>
                <p className="text-white/80 text-xs mb-1">{stat.title}</p>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Revenue Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-card">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-heading font-bold">Revenue Overview</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-muted-foreground mb-2">Online Payments</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{stats.online_revenue?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                <p className="text-sm text-muted-foreground mb-2">COD Revenue</p>
                <p className="text-2xl font-bold text-orange-600">
                  ₹{stats.cod_revenue?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="text-center p-4 bg-primary/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-2">Last 7 Days Orders</p>
                <p className="text-2xl font-bold text-primary">
                  {stats.recent_orders || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Alerts Section */}
          <div className="bg-white rounded-2xl p-6 shadow-card">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              <h2 className="text-xl font-heading font-bold">Alerts</h2>
            </div>
            
            <div className="space-y-4">
              {stats.pending_orders > 0 && (
                <Link to="/admin/orders" className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="font-medium text-orange-700">{stats.pending_orders} pending orders</p>
                    <p className="text-xs text-orange-600">Need attention</p>
                  </div>
                </Link>
              )}
              
              {stats.pending_orders === 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-green-700">All caught up!</p>
                    <p className="text-xs text-green-600">No pending orders</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h2 className="text-xl font-heading font-bold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <Link key={index} to={action.link}>
              <div 
                className="bg-white rounded-2xl p-6 shadow-card hover:shadow-hover transition-all h-full"
                data-testid={`action-card-${index}`}
              >
                {action.icon}
                <h3 className="text-lg font-heading font-bold mt-4 mb-2">{action.title}</h3>
                <p className="text-sm text-muted-foreground">{action.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
